import axios from 'axios';
import i18next, {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Header from '../components/Header';
import {Main} from '../components/Layout';
import Loading from '../components/Loading';
import ErrorPanel from '../components/ErrorPanel';
import {getPlayInfo, playTokenCheck} from '../utils/api';
import Game from './Game';

interface LocationState {
  sessionToken: string;
}

const Play = () => {
  const [clientName, setClientName] = useState('');
  const [clientLogo, setClientLogo] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [sessionToken, setSessionToken] = useState<string>('');
  const [gameStatus, setGameStatus] = useState<string>('');
  const [showAutoFlagReport, setShowAutoFlagReport] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<[]>([]);
  const [finalMessage, setFinalMessage] = useState<GameFinalMessageT>({
    it: '',
    en: '',
    es: '',
    pl: '',
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [playStatus, setPlayStatus] = useState<'loading' | 'error'>();

  const {token} = useParams();
  const location = useLocation();
  const state = location.state as LocationState;

  const setData = useCallback((response, skipMail: boolean) => {
    if (!skipMail) {
      setSessionToken(response.sessionToken);
    }
    setGameStatus(response.status);
    setClientName(response.project.customer.name);
    setClientLogo(response.project.customer.logo);
    setUserEmail(response.participant.email);
    setShowAutoFlagReport(response.autoFlagReport);
    setFinalMessage({
      it: response.project.finalMessageIt,
      en: response.project.finalMessageEn,
      es: response.project.finalMessageEs,
      pl: response.project.finalMessagePl,
    });
    setCustomFields(
      response.project.participantRegistryConfigs
        ? response.project.participantRegistryConfigs
        : []
    );

    i18next.changeLanguage(response.language);
  }, []);

  useEffect(() => {
    setPlayStatus('loading');

    if (state && state.sessionToken) {
      const sessionToken = state.sessionToken;
      setSessionToken(sessionToken);

      (async () => {
        try {
          const response = await getPlayInfo(sessionToken);
          setData(response, true);
          setPlayStatus(undefined);
        } catch (err) {
          console.log(err);

          if (axios.isAxiosError(err) && err.response?.status === 400) {
            setErrorMessage(t(err.response.data.message));
          } else {
            setErrorMessage(t('errors.link-not-valid'));
          }
          setPlayStatus('error');
        }
      })();

      return;
    }

    if (token) {
      (async () => {
        try {
          const response = await playTokenCheck(token);
          setData(response, false);
          setPlayStatus(undefined);
        } catch (err) {
          console.log(err);

          if (axios.isAxiosError(err) && err.response?.status === 400) {
            setErrorMessage(t(err.response.data.message));
          } else {
            setErrorMessage(t('errors.link-not-valid'));
          }
          setPlayStatus('error');
        }
      })();
    } else {
      setPlayStatus('error');
      setErrorMessage(t('errors.link-not-valid'));
    }
  }, [token, state, setData]);

  const renderMain = () => {
    if (playStatus === 'loading') {
      return <Loading />;
    }

    if (playStatus === 'error') {
      return <ErrorPanel errorMessage={errorMessage} />;
    }

    return (
      <Game
        sessionToken={sessionToken}
        status={gameStatus}
        userToken={token || ''}
        setGameStatus={setGameStatus}
        showAutoFlagReport={showAutoFlagReport}
        customFields={customFields}
        finalMessage={finalMessage}
      />
    );
  };

  return (
    <>
      <Header
        clientLogo={clientLogo}
        clientName={clientName}
        user={userEmail}
        sessionToken={sessionToken}
      />
      <Main>{renderMain()}</Main>
    </>
  );
};

export default Play;
